import { DropdownButtonType, List, ListItem, SingleSelectDropdown } from '@tableau/widgets';
import React = require('react');
import ReactMarkdown = require('react-markdown');
import { Messages } from '~src/codegen/Localize';
import { DialogAlertProps, InnerDialogAlertProps } from '~src/commonInfra/components/dialogAlerts/DialogAlerts';
import { DialogAlertStyles } from '~src/commonInfra/components/dialogAlerts/DialogAlertStyles';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { IOAuthConfigInfo } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';

import { AddServiceAccountCredential, ServiceAccountAuthState } from './AddServiceAccountCredentialDialog';

export type AuthModeState = {
    selectedConnectionAuthMode?: string;
    connAuthState?: ServiceAccountAuthState;
    errorMessage?: string;
}

export type AuthModeProps = {
    authOptionsList: ListItem[];
    parentState: AuthModeState;
}

export interface ListDialogAlertProps extends DialogAlertProps {
    errorListTitle: string;
    errors: string[];
}

export interface ListInnerDialogAlertProps extends InnerDialogAlertProps {
   listDialogProps: ListDialogAlertProps;
}

const ListDialogAlerts = (props: ListInnerDialogAlertProps) => (
    <div className={`${DialogAlertStyles.containerStyle} ${props.alertStyle}`} data-tb-test-id={props.testId}>
      <img
        src={imgPath(props.iconName)}
        draggable={false}
        alt={props.altText}
        className={DialogAlertStyles.image}
      />
      <span className={DialogAlertStyles.messageStyle}>
          <b>{props.message}</b><br></br>
          {props.listDialogProps.errorListTitle}
          <ul id="errorsList">
          {props.listDialogProps.errors.map(error =>
            <li key={error}>{<ReactMarkdown source={error}/>}</li>
          )}
          </ul>
      </span>
    </div>
  );

export const ListDialogErrorAlert = (props: ListDialogAlertProps) => (
    <ListDialogAlerts
      testId={props.testId}
      message={props.message}
      listDialogProps={props}
      iconName={'common_alert_error.svg'}
      alertStyle={DialogAlertStyles.errorStyle}
      altText={Messages.dialogAlert_errorAltText()}
    />
  );

    // Get the oauth credential "type" and then on submit executes the given OnSubmit callback.
    // for gbq, we are supporting service accounts now so when adding a new saved credential
    // for gbq, customers must select which auth type they would like to use.
export const showAddCredentialAuthModeDialog = (type: string, OnSubmit: (props: any, state: any, updateDialogState: any, type: string) => Promise<void>) => {
        // ideally we should get these values dynamically rather than them being hard coded like this.
    var authOptions = [{value: Messages.addNewSavedCredential_OAuth()}];

    if (FeatureFlags.snowflakeKeyPairAuth && type === 'snowflake') {
        authOptions.push({value: Messages.addNewSavedCredential_KeyPairAuth()});
    } else if (type === 'bigquery' || type === 'googlebigquery') {
        authOptions.push({value: Messages.addNewSavedCredential_ServiceAccount()});
    }

    let authModeState: AuthModeState = {
        selectedConnectionAuthMode: Messages.addNewSavedCredential_OAuth(),
        errorMessage: ''
    };
    let authModeProps: AuthModeProps = {
          authOptionsList: authOptions, parentState: authModeState};
    const modalInstance = openReactActionDialog<AuthModeProps, AuthModeState, void>({
        bodyComponent: GetAddCredentialAuthModeDialogBody,
        initialBodyProps: authModeProps,
        initialDialogState: authModeState,
        actionName: Messages.add(),
        action: (props, state, updateDialogState) => {return OnSubmit(props, state, updateDialogState, type);},
        dismissDialogOnActionFailed: false,
        isActionDelete: false,
        centerActionButtons: true,
        dialogWidth: TabDialogWidth.width375px,
        title: Messages.addNewSavedCredentialTitle(),
    });
    return modalInstance.result;
};

export const showAddCredentialIdpDialog = (type: string, oauthConfigList: IOAuthConfigInfo[], OnSubmit: (props: any, state: any, type: string, oauthConfigList: IOAuthConfigInfo[]) => Promise<void>) => {
    let authModeState: AuthModeState = {
        selectedConnectionAuthMode: oauthConfigList[0].oauthConfigId
    };
    let authOptionsList: ListItem[] = [];
    oauthConfigList.forEach(it=>{
        authOptionsList.push({value: it.oauthConfigId});
    });
    let authModeProps: AuthModeProps = {
        authOptionsList: authOptionsList, parentState: authModeState
    };
    const modalInstance = openReactActionDialog<AuthModeProps, AuthModeState, void>({
        bodyComponent: GetAddCredentialAuthModeDialogBody,
        initialBodyProps: authModeProps,
        initialDialogState: authModeState,
        actionName: Messages.add(),
        action: (props,state) => {return OnSubmit(props, state, type, oauthConfigList);},
        dismissDialogOnActionFailed: false,
        isActionDelete: false,
        centerActionButtons: true,
        dialogWidth: TabDialogWidth.width375px,
        title: Messages.addNewSavedCredentialTitle(),
    });
    return modalInstance.result;
};

export function GetAddCredentialAuthModeDialogBody(props: any, state: AuthModeState) {
    const [selectedConnectionAuthMode, setSelectedConnectionAuthMode] = React.useState(props.parentState.selectedConnectionAuthMode);

    const setSelectedConnectionAuthModes = (value: string) => {
        setSelectedConnectionAuthMode(value);
        props.parentState.selectedConnectionAuthMode = value;
    };

    return (<>
    {props.authOptionsList &&
        <div className='tb-login-text-box' data-tb-test-id='tb-authentication-drop-down'>
            <SingleSelectDropdown
                data-tb-test-id='tb-test-dropdown'
                className="tb-login-text-box"
                label={Messages.authentication()}
                type={DropdownButtonType.Outline}
                testId='auth-connection-auth-mode-select'
                value={selectedConnectionAuthMode}
                onChange={(value: string) => setSelectedConnectionAuthModes(value)}
                list={<List items={props.authOptionsList} />}
            >
                {selectedConnectionAuthMode}
            </SingleSelectDropdown>
        </div>
    }
    {selectedConnectionAuthMode === Messages.addNewSavedCredential_ServiceAccount() &&
        <div>
            <AddServiceAccountCredential
                authOptionsList={props.authOptionsList}
                parentState={props.parentState}
            >
            </AddServiceAccountCredential>
        </div>
    }
    {props.dialogState.errorMessage &&
    <ListDialogErrorAlert
        testId={'invalid-service-account-alert'}
        message={props.dialogState.errorMessage}
        errorListTitle={Messages.addNewSavedCredentialServiceAccountInvalidFile_WhatToTryNext()}
        errors={[Messages.addNewSavedCredentialServiceAccountInvalidFile_CheckPermissions(), Messages.addNewSavedCredentialServiceAccountInvalidFile_FormattingErrors(), Messages.addNewSavedCredentialServiceAccountInvalidFile_LogDetails()]}
    />}
    </>);
}
